import { auth } from '../firebase-config';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth';

// Sign up new user
export const signUp = async (email, password, displayName) => {
  try {
    console.log('🔐 Starting sign up process for:', email);
    
    // Basic validation
    if (!email || !password) {
      return { 
        success: false, 
        error: 'Please fill in all required fields.' 
      };
    }

    if (password.length < 6) {
      return { 
        success: false, 
        error: 'Password must be at least 6 characters long.' 
      };
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    console.log('✅ User created, updating profile...');
    
    // Update profile with display name
    if (displayName) {
      await updateProfile(user, {
        displayName: displayName
      });
    }

    console.log('📧 Sending verification email...');
    
    // Send email verification
    await sendEmailVerification(user);
    
    console.log('✅ Verification email sent to:', email);
    
    return { 
      success: true, 
      user,
      message: 'Account created successfully! Check your email for the verification link.'
    };
  } catch (error) {
    console.error('❌ Sign up error:', error.code, error.message);
    
    let errorMessage = 'An error occurred during sign up. Please try again.';
    
    // Firebase error codes
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'This email is already registered. Please sign in instead.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address.';
        break;
      case 'auth/weak-password':
        errorMessage = 'Password should be at least 6 characters long.';
        break;
      case 'auth/operation-not-allowed':
        errorMessage = 'Email/password accounts are not enabled. Please contact support.';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your internet connection.';
        break;
      default:
        errorMessage = error.message || 'Failed to create account. Please try again.';
    }
    
    return { 
      success: false, 
      error: errorMessage 
    };
  }
};

// Sign in existing user
export const signIn = async (email, password) => {
  try {
    console.log('🔐 Attempting sign in for:', email);
    
    // Basic validation
    if (!email || !password) {
      return { 
        success: false, 
        error: 'Please enter both email and password.' 
      };
    }

    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    console.log('✅ Sign in successful, checking verification...');
    
    // Check if email is verified
    if (!user.emailVerified) {
      console.log('⚠️ User not verified, sending new verification email...');
      
      // Send verification email again
      await sendEmailVerification(user);
      await signOut(auth);
      
      return { 
        success: false, 
        error: 'Please verify your email address before signing in. We have sent a new verification link to your email.',
        needsVerification: true
      };
    }
    
    console.log('✅ User verified, sign in complete');
    
    return { 
      success: true, 
      user,
      message: 'Successfully signed in! Welcome back.'
    };
  } catch (error) {
    console.error('❌ Sign in error:', error.code, error.message);
    
    let errorMessage = 'An error occurred during sign in. Please try again.';
    
    // Firebase error codes
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'No account found with this email. Please sign up first.';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Incorrect password. Please try again.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address.';
        break;
      case 'auth/invalid-credential':
        errorMessage = 'Invalid email or password. Please check your credentials.';
        break;
      case 'auth/user-disabled':
        errorMessage = 'This account has been disabled. Please contact support.';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many failed attempts. Please try again later.';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your internet connection.';
        break;
      case 'auth/operation-not-allowed':
        errorMessage = 'Email/password sign-in is not enabled. Please contact support.';
        break;
      default:
        errorMessage = error.message || 'Failed to sign in. Please check your email and password.';
    }
    
    return { 
      success: false, 
      error: errorMessage 
    };
  }
};

// Sign out user
export const logout = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Logout error:', error);
    return { success: false, error: error.message };
  }
};

// Send verification email
export const sendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    if (user) {
      await sendEmailVerification(user);
      return { 
        success: true,
        message: 'Verification email sent! Please check your inbox.'
      };
    }
    return { 
      success: false, 
      error: 'No user is currently signed in.' 
    };
  } catch (error) {
    console.error('Send verification error:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Check if user is verified
export const isUserVerified = () => {
  const user = auth.currentUser;
  return user && user.emailVerified;
};